import React, { useEffect, useState, useRef } from "react";
import { Box, Grid, Typography, Avatar, Container, Stack, Skeleton } from "@mui/material";
import RightSidebar from "../../pages/sidebar/SideBar";
import { MdArrowOutward } from "react-icons/md";
import { useNavigate, useSearchParams, useParams, Link } from "react-router-dom";
import { NewsStore } from "../../store/newsStore";
import { BASE_URL } from "../../api/api";
import dayjs from "dayjs";
import Preloader from "../preloader/PreLoader";
import CustomPagination from "../custom-pagination/CustomPagination";

const noImage = "/assets/logos/logo.png";

const LazyImage = ({ src, alt, sx, className, hideOnError }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    setLoaded(false);
    setError(false);
  }, [src]);

  useEffect(() => {
    if (imgRef.current && imgRef.current.complete) {
      if (imgRef.current.naturalWidth === 0) setError(true);
      else setLoaded(true);
    }
  }, [src]);

  if (error && hideOnError) return null;

  const displaySrc = error ? noImage : src;

  return (
    <Box sx={{ position: "relative", flexShrink: 0, ...sx }}>
      {!loaded && !error && (
        <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" sx={{ position: "absolute", top: 0, left: 0, zIndex: 1, borderRadius: sx?.borderRadius }} />
      )}
      <Box
        component="img"
        ref={imgRef}
        className={className}
        src={displaySrc}
        alt={alt}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
        sx={{
          width: "100%", height: "100%",
          objectFit: displaySrc === noImage ? "contain" : (sx?.objectFit || "cover"),
          objectPosition: sx?.objectPosition || "center center",
          borderRadius: sx?.borderRadius, opacity: loaded || error ? 1 : 0, transition: "opacity 0.4s ease-in-out",
          bgcolor: displaySrc === noImage ? "rgba(0,0,0,0.05)" : "transparent",
        }}
      />
    </Box>
  );
};

export default function ChhattisgarhNews() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("q");
  const cityQuery = searchParams.get("city");
  const { fetchNews, news, totalPages } = NewsStore();
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const categoryNames = {
    "chhattisgarh": "छत्तीसगढ़",
    "madhya-pradesh": "मध्य प्रदेश",
    "entertainment": "मनोरंजन",
    "sports": "खेल",
    "religion": "धर्म",
    "viral-videos": "वायरल वीडियो",
    "world": "विदेश"
  };

  const cityMapping = {
    "रायपुर": "Raipur",
    "बिलासपुर": "Bilaspur",
    "दुर्ग": "Durg",
    "भिलाई": "Bhilai",
    "कोरबा": "Korba",
    "रायगढ़": "Raigarh",
    "जगदलपुर": "Jagdalpur",
    "अंबिकापुर": "Ambikapur"
  };

  const pageTitle = cityQuery
    ? cityQuery
    : searchQuery
      ? `Search Results for "${searchQuery}"`
      : (categoryNames[slug] || slug?.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase()) || "Latest News");

  useEffect(() => {
    setCurrentPage(1);
  }, [slug, searchQuery, cityQuery]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await fetchNews(currentPage, 10, searchQuery || "", slug || "", cityQuery || "");
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [slug, searchQuery, currentPage, cityQuery]);

  const truncateHtmlText = (html, maxLength = 150) => {
    const div = document.createElement('div')
    div.innerHTML = html
    const text = div.textContent || div.innerText || ''
    return text.length > maxLength
      ? text.substring(0, maxLength) + '...'
      : text
  }

  const filteredNews = news?.filter((item) => {
    if (!cityQuery) return true;
    const mappedCity = cityMapping[cityQuery] || cityQuery;
    return item.location?.city?.toLowerCase() === mappedCity.toLowerCase();
  });

  if (loading) return <Preloader />;

  return (
    <>
      <Box
        sx={{
          width: "100%",
          py: { xs: 4, sm: 6 },
          mt: { xs: 18, sm: 18, md: 19, lg: 17 },
          backgroundColor: (theme) =>
            theme.palette.mode === "light"
              ? "#ffffff" // Light mode background
              : "#121212", // Dark mode background
          backgroundImage: (theme) =>
            theme.palette.mode === "light"
              ? "radial-gradient(#d5d5d5 1px, transparent 1px)"
              : "radial-gradient(#333 1px, transparent 1px)", // Dark mode gradient
          backgroundSize: "18px 18px",
        }}
      >
        <Container>
          <Box sx={{ px: { xs: 0, sm: 6 } }}>
            {/* Heading */}
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 2,
                textAlign: "left",
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                color: "text.primary"
              }}
            >
              {pageTitle}
            </Typography>

            {/* Description */}
            {!searchQuery && (
              <Typography
                variant="body1"
                sx={{
                  textAlign: "left",
                  color: "text.primary",
                  maxWidth: "900px",
                  lineHeight: 1.6,
                  fontSize: { xs: "14px", sm: "16px" },
                }}
              >
                {pageTitle} News – Find latest {pageTitle} news headlines, breaking news, and updates exclusively from{" "}
                <a
                  href="https://janpathnews24.com"
                  style={{ color: "#000", textDecoration: "underline" }}
                >
                  janpathnews24.com
                </a>
              </Typography>
            )}

            {/* Find More Section */}
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ mt: 3, flexWrap: "wrap" }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <MdArrowOutward color="#ff004f" size={20} />
                <Typography
                  variant="body1"
                  sx={{
                    color: "#ff004f",
                    fontWeight: 600,
                    fontSize: { xs: "14px", sm: "15px" },
                  }}
                >
                  Find More:
                </Typography>
              </Stack>

              <Typography sx={{ fontWeight: 600, color: "text.primary" }}>BJP</Typography>
              <Typography sx={{ fontWeight: 600, color: "text.primary" }}>BUDGET</Typography>
              <Typography sx={{ fontWeight: 600, color: "text.primary" }}>CONG</Typography>
              <Typography sx={{ fontWeight: 600, color: "text.primary" }}>विधानसभा</Typography>
            </Stack>
          </Box>
        </Container>
      </Box>

      <Box sx={{ minHeight: "100vh", bgcolor: "background.default", color: "text.primary" }}>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Box sx={{ width: "100%", py: 4, px: { xs: 2, md: 6 } }}>
            <Grid
              container
              spacing={4}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
                gap: 4,
              }}
            >
              {/* CENTER MAIN CONTENT */}
              <Grid item xs={12} md={8}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: "bold",
                    mb: 3,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ width: 4, height: 25, bgcolor: "red", mr: 1 }} />
                  {searchQuery ? "Latest Search Results" : `Latest ${pageTitle}`}
                </Typography>

                {filteredNews?.length > 0 ? (filteredNews.map((item) => (
                  <Link
                    to={`/${item.slug}`}
                    key={item.id}
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        gap: 2,
                        mb: 4,
                        p: 4,
                        // border: "1px solid #eee",
                        pb: 2,
                        boxShadow: 1,
                        borderRadius: "10px"

                      }}
                    >
                      {/* LEFT TEXT */}
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            mb: 1,
                            cursor: "pointer",
                            "&:hover": { color: "text.primary" },
                          }}
                        >
                          {item.title}
                        </Typography>

                        <Typography variant="body2" sx={{ color: "text.primary", mb: 1 }}>
                          {truncateHtmlText(item.description)}
                        </Typography>

                        {/* AUTHOR */}
                        <Box
                          sx={{ display: "flex", alignItems: "center", mt: 1 }}
                        >
                          <Avatar src={item.user?.profile_image || noImage} sx={{ width: 26, height: 26, mr: 1 }} />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.user?.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "text.primary", ml: 1 }}
                          >
                            • {dayjs(item.published_at).format("MMMM D, YYYY")}
                          </Typography>
                        </Box>
                      </Box>

                      {/* RIGHT IMAGE */}
                      <Box
                        sx={{
                          width: { xs: "100%", sm: 220, md: 240 },
                          height: { xs: 180, sm: 140, md: 150 },
                          position: "relative",
                          flexShrink: 0,
                        }}
                      >
                        <LazyImage
                          src={item.image ? (item.image.startsWith("http") ? item.image : `${BASE_URL.replace(/\/$/, "")}/${item.image.replace(/^\//, "")}`) : noImage}
                          alt={item.title}
                          hideOnError={false}
                          sx={{
                            width: "100%",
                            height: "100%",
                            borderRadius: "8px",
                          }}
                        />

                        {/* TAGS */}
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 8,
                            left: 8,
                            display: "flex",
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              bgcolor: "red",
                              color: "#fff",
                              px: 1.2,
                              py: 0.3,
                              fontSize: "11px",
                              borderRadius: 1,
                              fontWeight: "bold",
                            }}
                          >
                            {item.category?.name}
                          </Box>

                        </Box>
                      </Box>
                    </Box>
                  </Link>
                ))) : (
                  <Typography variant="h6" sx={{ textAlign: "center", mt: 4, color: "text.secondary" }}>
                    कोई समाचार उपलब्ध नहीं है
                  </Typography>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CustomPagination
                      totalPages={totalPages}
                      currentPage={currentPage}
                      onPageChange={(page) => setCurrentPage(page)}
                    />
                  </Box>
                )}
              </Grid>

              {/* RIGHT SIDEBAR */}
              {/* <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    position: "sticky",
                    top: 80,
                    width: "100%",
                  }}
                >
                  <RightSidebar />
                </Box>
              </Grid> */}
            </Grid>
          </Box>
        </Container>
      </Box>
    </>
  );
}
